import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Form, Modal, Button, Card, Row, Col, Badge, Table, Alert } from "react-bootstrap";
import { BASE_URL } from "../../../env";
import { calculateEmiAmountRaw } from "../../../utils/utils";
import "./ApproveCreditManagerModal.css";

const ApproveCreditManagerModal = ({
  show,
  handleConfirm,
  handleClose,
  memberId,
  creditManagerId,
  proposedLoanAmount,
}) => {
  const [comment, setComment] = useState("");
  // const [loanAmount, setLoanAmount] = useState("");
  const [validationError, setValidationError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [coApplicantSelectedFile, setCoApplicantSelectedFile] = useState(null);
  const [pdf1SelectedFile, setPdf1SelectedFile] = useState(null);
  const [pdf2SelectedFile, setPdf2SelectedFile] = useState(null);

  const initialCalculations = {
    memberTotalIncome: 0,
    memberTotalMonthlyEMI: 0,
    memberTotalHouseHoldExpenses: 0,
    memberOtherExpenses: 0,
    memberMonthlyBalanceAmount: 0,
    expectedLoanAmount: 0,
    rateOfInterest: 26,
    tenureInMonths: 0,
    expectedMonthlyEmi: 0,
    coApplicantIncome: 0,
    coCoApplicantIncome: 0,
    coOtherIncome: 0,
    coBusinessExpenses: 0,
    coHouseHoldExpenses: 0,
    coOtherExpenses: 0,
    coExistingEmi: 0,
    coBalance: 0,
    eligibility: false,
  };
  const [calculations, setCalculations] = useState(initialCalculations);
  const [memberDetails, setMemberDetails] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [generatedSchedule, setGeneratedSchedule] = useState([]);

  const fetchList = useCallback(async () => {
    if (!memberId) {
      console.log("Invalid memberId");
      return;
    }
    try {
      const response = await axios.get(
        `${BASE_URL}/api/loan/formdetails/${memberId}`
      );
      setMemberDetails(response.data);
      setCalculations((prev) => ({
        ...prev,
        rateOfInterest: response.data.proposedLoanDetails?.rateOfInterest || 0,
      }));
    } catch (error) {
      console.log("Error fetching list:", error);
    }
  }, [memberId]);

  useEffect(() => {
    if (memberId) {
      fetchList();
    }
  }, [memberId, fetchList]);

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleCoApplicantFileChange = (event) => {
    setCoApplicantSelectedFile(event.target.files[0]);
  };

  const handlePdf1FileChange = (event) => {
    setPdf1SelectedFile(event.target.files[0]);
  };

  const handlePdf2FileChange = (event) => {
    setPdf2SelectedFile(event.target.files[0]);
  };

  const performCalculations = useCallback(() => {
    if (memberDetails) {
      let totalMonthlyIncome = 0;
      totalMonthlyIncome += parseInt(
        memberDetails.businessDetails.monthlyBusinessIncome || 0
      );

      if (
        memberDetails.familyBusinessDetails.sourceOfIncome === "Salaried Person"
      ) {
        totalMonthlyIncome +=
          parseInt(memberDetails.familyBusinessDetails.monthlySalary) || 0;
      } else if (
        memberDetails.familyBusinessDetails.sourceOfIncome === "Business Person"
      ) {
        totalMonthlyIncome +=
          parseInt(memberDetails.familyBusinessDetails.monthlyBusinessIncome) ||
          0;
      }

      const totalEMI = parseInt(memberDetails.loanDetails.totalmonthlyEmi) || 0;

      // const houseHoldExpenses = calculations.memberTotalHouseHoldExpenses;
      // const otherExpenses = calculations.memberOtherExpenses;
      // const houseHoldExpenses =
      //   parseInt(memberDetails.proposedLoanDetails.houseHoldExpenses) || 0;
      // const otherExpenses =
      //   parseInt(memberDetails.proposedLoanDetails.otherExpenses) || 0;

      // const balanceAmount =
      //   totalMonthlyIncome - totalEMI - houseHoldExpenses - otherExpenses;
      const balanceAmount = parseInt(memberDetails.memberDetails.balance) || 0;

      const expectedMonthlyEmi = Math.round(calculateEmiAmountRaw(
        calculations.expectedLoanAmount,
        calculations.rateOfInterest,
        calculations.tenureInMonths
      ));

      const isEligible = expectedMonthlyEmi <= balanceAmount;

      setCalculations((prev) => ({
        ...prev,
        memberId: memberId,
        creditManagerId: creditManagerId,
        memberTotalIncome: totalMonthlyIncome,
        memberTotalMonthlyEMI: totalEMI,
        memberMonthlyBalanceAmount: balanceAmount,
        expectedMonthlyEmi: isFinite(expectedMonthlyEmi) ? expectedMonthlyEmi : 0,
        eligibility: isEligible,
      }));
    }
  }, [
    memberDetails,
    memberId,
    creditManagerId,
    calculations.expectedLoanAmount,
    calculations.rateOfInterest,
    calculations.tenureInMonths,
  ]);

  useEffect(() => {
    if (memberDetails) {
      performCalculations();
    }
  }, [memberDetails, performCalculations]);

  const handleGenerateSchedule = () => {
    const { expectedLoanAmount, rateOfInterest, tenureInMonths } = calculations;
    const monthlyRate = rateOfInterest / 12 / 100;
    const emi = calculateEmiAmountRaw(expectedLoanAmount, rateOfInterest, tenureInMonths);

    let balance = expectedLoanAmount;
    const schedule = [];

    for (let month = 1; month <= tenureInMonths; month++) {
      const interest = balance * monthlyRate;
      const principal = emi - interest;
      balance -= principal;

      schedule.push({
        month,
        emi,
        principal,
        interest,
        balance: balance > 0 ? balance : 0,
      });
    }
    setGeneratedSchedule(schedule);
    setShowSchedule(true);
  };

  const handleConfirmApprove = async () => {
    try {
      if (!memberId) {
        setValidationError("Member ID is required.");
        console.warn("Validation failed: Member ID is missing");
        return;
      }
      if (!creditManagerId) {
        setValidationError("Credit Manager ID is required.");
        console.warn("Validation failed: Credit Manager ID is missing");
        // Also check if creditManagerId is passed as prop correctly
        return;
      }
      if (!comment) {
        setValidationError("Credit Manager Message is required.");
        console.warn("Validation failed: Comment is missing");
        return;
      }
      if (!selectedFile) {
        setValidationError("Cb Report file is required.");
        console.warn("Validation failed: Cb Report file is missing");
        return;
      }
      const fileType = selectedFile.type;

      if (fileType !== "application/pdf" && !selectedFile.name.toLowerCase().endsWith(".pdf")) {
        setValidationError("Only PDF files are allowed.");
        return;
      }
      if (coApplicantSelectedFile) {
        const coApplicantFileType = coApplicantSelectedFile.type;
        if (coApplicantFileType !== "application/pdf" && !coApplicantSelectedFile.name.toLowerCase().endsWith(".pdf")) {
          setValidationError("Only PDF files are allowed.");
          return;
        }
      }
      if (pdf1SelectedFile) {
        const pdf1FileType = pdf1SelectedFile.type;
        if (pdf1FileType !== "application/pdf" && !pdf1SelectedFile.name.toLowerCase().endsWith(".pdf")) {
          setValidationError("Only PDF files are allowed.");
          return;
        }
      }
      if (pdf2SelectedFile) {
        const pdf2FileType = pdf2SelectedFile.type;
        if (pdf2FileType !== "application/pdf" && !pdf2SelectedFile.name.toLowerCase().endsWith(".pdf")) {
          setValidationError("Only PDF files are allowed.");
          return;
        }
      }

      setIsSubmitting(true);
      const formData = new FormData();
      formData.append("member_id", memberId);
      formData.append("creditManagerId", creditManagerId);
      formData.append("creditManagerMessage", comment);
      formData.append(
        "sanctionedLoanAmountByCreditManager",
        calculations.expectedLoanAmount
      );
      formData.append("cbReport", selectedFile);
      if (coApplicantSelectedFile) {
        formData.append("coApplicantCbReport", coApplicantSelectedFile);
      }
      if (pdf1SelectedFile) {
        formData.append("pdf1", pdf1SelectedFile);
      }
      if (pdf2SelectedFile) {
        formData.append("pdf2", pdf2SelectedFile);
      }


      const response = await axios.post(
        `${BASE_URL}/api/uploadCbReport`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
          timeout: 120000, // 2 minutes timeout for slow LAN uploads
        }
      );

      if (response.data.message) {
        setValidationError("");
        handleConfirm();
      } else {
        console.log("error in response: " + response.data.error);
        const errData = response.data.error;
        setValidationError(typeof errData === 'object' ? JSON.stringify(errData) : (errData || "Server returned an unsuccessful response"));
      }
    } catch (error) {
      console.error("error in catch: ", error);
      let errorMsg = "An error occurred";
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        errorMsg = error.response.data?.error || error.response.data?.message || `Server Error (${error.response.status})`;
      } else if (error.request) {
        // The request was made but no response was received
        errorMsg = "Network Error: No response from server. Please check if the backend is running at " + BASE_URL;
      } else {
        // Something happened in setting up the request that triggered an Error
        errorMsg = error.message;
      }
      
      if (typeof errorMsg === 'object') {
        errorMsg = JSON.stringify(errorMsg);
      }
      setValidationError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="full-width-modal"
      contentClassName="cm-approve-modal"
    >
      <Modal.Header closeButton className="cm-approve-modal__header">
        <Modal.Title>
          <span className="cm-approve-modal__eyebrow">Credit Manager Review</span>
          <span className="cm-approve-modal__title">Loan Assessment Confirmation</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="cm-approve-modal__body">
        <Row className="g-3 mb-3">
          <Col lg={5}>
            <Card className="cm-card h-100">
              <Card.Header className="cm-card__header">Applicant Financials</Card.Header>
              <Card.Body>
                <h6 className="cm-subheading">CRO Entered Data</h6>
                <dl className="cm-kv mb-3">
                  <div className="cm-kv__row">
                    <dt>Total Income</dt>
                    <dd>{calculations.memberTotalIncome}</dd>
                  </div>
                  <div className="cm-kv__row">
                    <dt>Total Monthly EMI</dt>
                    <dd>{calculations.memberTotalMonthlyEMI}</dd>
                  </div>
                </dl>

                <h6 className="cm-subheading">Credit Officer Entered Data</h6>
                <dl className="cm-kv">
                  <div className="cm-kv__row">
                    <dt>Applicant Income</dt>
                    <dd>{memberDetails && memberDetails.memberDetails.applicantIncome}</dd>
                  </div>
                  <div className="cm-kv__row">
                    <dt>Co Applicant Income</dt>
                    <dd>{memberDetails && memberDetails.memberDetails.coApplicantIncome}</dd>
                  </div>
                  <div className="cm-kv__row">
                    <dt>Other Income</dt>
                    <dd>{memberDetails && memberDetails.memberDetails.otherIncome}</dd>
                  </div>
                  <div className="cm-kv__row">
                    <dt>Business Expenses</dt>
                    <dd>{memberDetails && memberDetails.memberDetails.businessExpenses}</dd>
                  </div>
                  <div className="cm-kv__row">
                    <dt>Household Expenses</dt>
                    <dd>{memberDetails && memberDetails.memberDetails.houseHoldExpenses}</dd>
                  </div>
                  <div className="cm-kv__row">
                    <dt>Other Expenses</dt>
                    <dd>{memberDetails && memberDetails.memberDetails.otherExpenses}</dd>
                  </div>
                  <div className="cm-kv__row">
                    <dt>Existing EMI</dt>
                    <dd>{memberDetails && memberDetails.memberDetails.existingEmi}</dd>
                  </div>
                  <div className="cm-kv__row cm-kv__row--highlight">
                    <dt>Balance Amount</dt>
                    <dd>{memberDetails && memberDetails.memberDetails.balance}</dd>
                  </div>
                </dl>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={7}>
            <Card className="cm-card h-100">
              <Card.Header className="cm-card__header">Loan Terms &amp; Eligibility</Card.Header>
              <Card.Body>
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Loan Amount</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Enter Expected Loan Amount"
                        value={calculations.expectedLoanAmount}
                        onChange={(event) => {
                          setCalculations((prev) => ({
                            ...prev,
                            expectedLoanAmount: event.target.value,
                          }));
                          performCalculations();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.preventDefault()}
                        onFocus={(e) => {
                          e.target.addEventListener(
                            "wheel",
                            (e) => e.preventDefault(),
                            {
                              passive: false,
                            }
                          );
                        }}
                        onBlur={(e) => {
                          e.target.removeEventListener("wheel", (e) =>
                            e.preventDefault()
                          );
                        }}
                        step="1"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Rate of Interest (%)</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Enter Rate of Interest (%)"
                        value={calculations.rateOfInterest}
                        onChange={(event) => {
                          setCalculations((prev) => ({
                            ...prev,
                            rateOfInterest: event.target.value,
                          }));
                          performCalculations();
                        }}
                        readOnly={true}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.preventDefault()}
                        onFocus={(e) => {
                          e.target.addEventListener(
                            "wheel",
                            (e) => e.preventDefault(),
                            {
                              passive: false,
                            }
                          );
                        }}
                        onBlur={(e) => {
                          e.target.removeEventListener("wheel", (e) =>
                            e.preventDefault()
                          );
                        }}
                        step="1"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Tenure (in months)</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Enter Tenure (in months)"
                        value={calculations.tenureInMonths}
                        onChange={(event) => {
                          setCalculations((prev) => ({
                            ...prev,
                            tenureInMonths: event.target.value,
                          }));
                          performCalculations();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.preventDefault()}
                        onFocus={(e) => {
                          e.target.addEventListener(
                            "wheel",
                            (e) => e.preventDefault(),
                            {
                              passive: false,
                            }
                          );
                        }}
                        onBlur={(e) => {
                          e.target.removeEventListener("wheel", (e) =>
                            e.preventDefault()
                          );
                        }}
                        step="1"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="cm-result-bar mt-4">
                  <div className="cm-result-bar__metric">
                    <span className="cm-result-bar__label">Expected Monthly EMI</span>
                    <span className="cm-result-bar__value">
                      {calculations.expectedMonthlyEmi.toFixed(2)}
                    </span>
                  </div>
                  <div className="cm-result-bar__metric">
                    <span className="cm-result-bar__label">Eligibility</span>
                    <Badge
                      bg={calculations.eligibility ? "success" : "danger"}
                      className="cm-badge"
                    >
                      {calculations.eligibility ? "Eligible" : "Not Eligible"}
                    </Badge>
                  </div>
                  <Button className="cm-btn-generate" onClick={handleGenerateSchedule}>
                    Generate Schedule
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {showSchedule && (
          <Card className="cm-card mb-3">
            <Card.Header className="cm-card__header">Repayment Schedule</Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive cm-schedule-scroll">
                <Table striped bordered hover className="mb-0 cm-schedule-table text-center">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Month</th>
                      <th>EMI</th>
                      <th>Principal</th>
                      <th>Interest</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generatedSchedule.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{item.month}</td>
                        <td>{Math.round(item.emi)}</td>
                        <td>{Math.round(item.principal)}</td>
                        <td>{Math.round(item.interest)}</td>
                        <td>{Math.round(item.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        )}

        <Row className="g-3">
          <Col lg={7}>
            <Card className="cm-card h-100">
              <Card.Header className="cm-card__header">
                Credit Bureau Reports &amp; Documents
              </Card.Header>
              <Card.Body>
                <Row className="g-3">
                  <Col md={6}>
                    <div className="cm-file-field">
                      <span className="cm-file-field__label">Applicant CB Report</span>
                      <input type="file" onChange={handleFileChange} />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="cm-file-field">
                      <span className="cm-file-field__label">
                        Co-Applicant CB Report{" "}
                        <span className="cm-file-field__optional">(optional)</span>
                      </span>
                      <input type="file" onChange={handleCoApplicantFileChange} />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="cm-file-field">
                      <span className="cm-file-field__label">
                        PDF 1 <span className="cm-file-field__optional">(optional)</span>
                      </span>
                      <input type="file" onChange={handlePdf1FileChange} />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="cm-file-field">
                      <span className="cm-file-field__label">
                        PDF 2 <span className="cm-file-field__optional">(optional)</span>
                      </span>
                      <input type="file" onChange={handlePdf2FileChange} />
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={5}>
            <Card className="cm-card h-100">
              <Card.Header className="cm-card__header">Approval</Card.Header>
              <Card.Body>
                <Form.Group>
                  <Form.Label>Comment</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Comment"
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    required
                  />
                </Form.Group>
                <Row className="g-3 mt-1">
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>Proposed Loan Amount</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Enter Loan Amount"
                        value={proposedLoanAmount}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.preventDefault()}
                        onFocus={(e) => {
                          e.target.addEventListener(
                            "wheel",
                            (e) => e.preventDefault(),
                            {
                              passive: false,
                            }
                          );
                        }}
                        onBlur={(e) => {
                          e.target.removeEventListener("wheel", (e) =>
                            e.preventDefault()
                          );
                        }}
                        step="1"
                        readOnly
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>Sanctioned Loan Amount</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Enter Loan Amount"
                        value={calculations.expectedLoanAmount}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.preventDefault()}
                        onFocus={(e) => {
                          e.target.addEventListener(
                            "wheel",
                            (e) => e.preventDefault(),
                            {
                              passive: false,
                            }
                          );
                        }}
                        onBlur={(e) => {
                          e.target.removeEventListener("wheel", (e) =>
                            e.preventDefault()
                          );
                        }}
                        step="1"
                        readOnly
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        {validationError && (
          <Alert variant="danger" className="w-100 py-2 mb-0 me-auto">
            {validationError}
          </Alert>
        )}
        <Button className="cm-btn-cancel" onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button className="cm-btn-submit" onClick={handleConfirmApprove} disabled={isSubmitting}>
          {isSubmitting ? "Submitting..." : "Submit"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ApproveCreditManagerModal;
